import { Navigate, RouteObject } from "react-router-dom";
import { NotFound } from "src/routes/NotFound";
import { PersonalDashboardPage } from "src/routes/personal-dashboard/PersonalDashboard";
import { AppShell } from "src/routing/components/AppShell";
import { GlobalNavLayout } from "src/routing/components/layouts/GlobalNavLayout";
import {
  personalDashboardActionsRoute,
  personalDashboardActivityRoute,
  personalDashboardRoute,
  personalDashboardToDosRoute,
} from "src/routing/domains/personalDashboardPaths";

/**
 * Personal Dashboard route trees (`/`, `/personal-dashboard/*`, `/my-blueprint/*`).
 *
 * Role: data router domain route.
 *
 * Legacy stack without `ScrollableParentLayout` (page previously opted out of PageLayout's
 * scroll frame via `hasOwnLayout`): `AppShell` → `GlobalNavLayout` → page.
 * When the page moves to `PageHeaderLayout` / `GridTableLayout`, switch to
 * `AppShell` → `BeamNavbarLayout` → page (see `domains/projects.ts`).
 *
 * Path constants live in `personalDashboardPaths.ts` (imported by `RouteUrls.ts` / pages) to avoid a
 * cycle through this file's page `Component` imports.
 */
const rootIndexRoute: RouteObject = {
  path: "/",
  Component: AppShell,
  children: [{ index: true, Component: RootIndexRedirect }],
};

/** Post-login landing: honor a saved deep link, otherwise go to Personal Dashboard. */
function RootIndexRedirect() {
  const deepLink = localStorage.getItem("deepLink");
  if (deepLink) {
    localStorage.removeItem("deepLink");
    return <Navigate to={deepLink} replace />;
  }
  return <Navigate to={personalDashboardRoute.url()} replace />;
}

const personalDashboardDomainRoute: RouteObject = {
  path: personalDashboardRoute.path,
  Component: AppShell,
  children: [
    {
      Component: GlobalNavLayout,
      children: [
        { index: true, element: <Navigate to={personalDashboardActionsRoute.url()} replace /> },
        { path: `${personalDashboardRoute.path}/:section/*`, Component: PersonalDashboardPage },
      ],
    },
    { path: "*", Component: NotFound },
  ],
};

/** Historical `/my-blueprint` bookmarks → Personal Dashboard sections. */
const myBlueprintRedirectRoute: RouteObject = {
  path: "/my-blueprint",
  Component: AppShell,
  children: [
    { index: true, element: <Navigate to={personalDashboardActionsRoute.url()} replace /> },
    { path: "/my-blueprint/to-dos", element: <Navigate to={personalDashboardToDosRoute.url()} replace /> },
    { path: "/my-blueprint/activity-feed", element: <Navigate to={personalDashboardActivityRoute.url()} replace /> },
  ],
};

/**
 * Migrated Personal Dashboard route trees registered in `Router.tsx`.
 */
export const personalDashboardRoutes = [rootIndexRoute, personalDashboardDomainRoute, myBlueprintRedirectRoute];
